import React from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { Tag } from 'primereact/tag';
import { RebateDetail, ScenarioResult } from '../../types';
import './RebateDetailDialog.css';

// Which scenario + product family the dialog is open on. Kept separate from `visible`
// so closing can animate out with its content still mounted.
export interface RebateDetailTarget {
  scenarioId: number;
  pfId: string;
  pfLabel: string;
}

interface RebateDetailDialogProps {
  visible: boolean;
  onHide: () => void;
  // Fired after the exit transition; the caller clears its target here so the body
  // doesn't blank before the dialog has finished closing.
  onExited: () => void;
  target: RebateDetailTarget | null;
  // Drill-down rows for the target scenario + product family only — the caller filters.
  rebateDetailRows: RebateDetail[];
  // The matching summary row: supplies total_direct_rebate, direct_rebate_per_eu,
  // category_units and total_rebate for the header, direct-rebate card and divergence check.
  summaryRow?: ScenarioResult | null;
  // Header subtitle after the product family label. Defaults to 'Current' / 'Scenario N';
  // Favorites passes its category instead, since a favorite's scenario_id is meaningless there.
  scenarioLabel?: string;
}

const formatCurrency = (value: number | null | undefined): string => {
  if (value === null || value === undefined) return 'N/A';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

const formatNumber = (value: number | null | undefined): string => {
  if (value === null || value === undefined) return 'N/A';
  return value.toLocaleString();
};

// Rebate drill-down dialog: total header plus one card per contract opportunity (a tiered
// one renders its ladder), shared by Scenario Results and Favorites. Extracted verbatim
// from ScenarioResults.tsx; the comments below predate the extraction and still apply.
const RebateDetailDialog: React.FC<RebateDetailDialogProps> = ({
  visible,
  onHide,
  onExited,
  target,
  rebateDetailRows,
  summaryRow,
  scenarioLabel,
}) => {
  return (
    <Dialog
      header={target ? (
        <div>
          <div>Rebate Detail</div>
          <div className="text-sm font-normal text-600 mt-1">
            {target.pfLabel} · {scenarioLabel ?? (target.scenarioId === 0 ? 'Current' : `Scenario ${target.scenarioId}`)}
          </div>
        </div>
      ) : 'Rebate Detail'}
      visible={visible}
      onHide={onHide}
      // Clearing the target is what unmounts the body, so it waits for the exit transition;
      // doing both at once blanked the dialog before it had finished closing.
      transitionOptions={{ timeout: { enter: 150, exit: 150 }, onExited }}
      style={{ width: '100vw', maxWidth: '1080px' }}
      breakpoints={{ '960px': '100vw' }}
      modal
      dismissableMask={true}
      blockScroll
      // Padding on the content, not the footer: the content is the scroll container.
      contentClassName="rebate-detail-content"
      contentStyle={{ maxHeight: '75vh', overflow: 'auto', paddingBottom: '1rem' }}
      // No autoFocus — the focus ring would sit on the button for as long as the dialog is open.
      footer={
        <div className="pt-2">
          <Button label="Close" onClick={onHide} />
        </div>
      }
    >
      {target && (() => {
        // Same predicate pfRebateEarning applies, which is what guarantees this is non-empty:
        // the link only renders for pairs in that set, so there is no empty state to fall back to.
        //
        // 'Excluded' rows are opportunities ScenarioIQ cannot model automatically, which the user
        // may credit an estimated rebate for. Every candidate the account has is shown, credited
        // or not, with the same tier UI as a contract -- an uncredited one is still an assumption
        // worth seeing. A credited tier carries a rebate; the rest report a dash.
        const rows = rebateDetailRows.filter(r => r.has_contract || r.rebate_source === 'Excluded');

        // The SP applies no ORDER BY, so without this the order is whatever it produced.
        const sorted = rows.slice().sort((a, b) => {
          const typeRank = (r: RebateDetail) => (r.rebate_source === 'Excluded' ? 1 : 0);
          return (
            typeRank(a) - typeRank(b) ||
            (a.opportunity ?? '').localeCompare(b.opportunity ?? '') ||
            (a.tier ?? 0) - (b.tier ?? 0)
          );
        });

        const contractEarned = rows.reduce((s, r) => s + Number(r.rebate_earned || 0), 0);
        // Count opportunities (ladders), not tier rows: a 3-tier ladder that pays is one
        // projecting opportunity, not "1 of 3".
        const opportunityKeyOf = (r: RebateDetail) =>
          r.rebate_source === 'Excluded'
            ? `excl:${r.opportunity ?? r.product_family_id}`
            : `coid:${r.contract_opportunities_id ?? 'none'}`;
        const opportunityCount = new Set(rows.map(opportunityKeyOf)).size;
        const projectingCount = new Set(
          rows.filter(r => Number(r.rebate_earned || 0) > 0).map(opportunityKeyOf)
        ).size;

        // The direct rebate is not an opportunity: the SP echoes the user's per-EU input back
        // on EVERY row of the family (one per contract x tier) and extends it to
        // TotalDirectRebate, so it arrives as a column on the result row rather than as a
        // rebate row with a contract and tiers. The backend takes it as a max per family and
        // adds it into total_rebate exactly once, which is why it must not be summed off the
        // rows here either -- a 3-row family would report it 3 times. Shown as its own line
        // so the total stays legible, and kept out of the opportunity counts.
        const directRebate = Number(summaryRow?.total_direct_rebate ?? 0);
        const directRebatePerEu = Number(summaryRow?.direct_rebate_per_eu ?? 0);
        const hasDirectRebate = directRebate > 0;
        const totalEarned = contractEarned + directRebate;

        // Both figures cover the same ground -- summed contract rows plus the one direct
        // rebate, which is exactly how the backend builds total_rebate -- so a divergence
        // means the two paths have drifted apart.
        const summaryRebate = summaryRow?.total_rebate != null ? Number(summaryRow.total_rebate) : null;
        const rebatesDiverge = summaryRebate != null && Math.abs(summaryRebate - totalEarned) >= 1;

        // An em dash, not the word "None": these slots are inapplicable rather than empty --
        // an excluded opportunity has no threshold or contract units, and a single-leg
        // contract has no secondary. Matches the dash the card already shows for no rebate.
        const NONE = '—';

        // RowType stated once here rather than repeated on every card. 'Excluded' counts every
        // candidate, credited or not, so this cannot claim they were credited.
        const generatedCount = rows.filter(r => r.rebate_source !== 'Excluded').length;
        const excludedCount = rows.length - generatedCount;
        const metaText = excludedCount === 0
          ? (rows.length === 1 ? 'Auto-generated' : rows.length === 2 ? 'Both auto-generated' : 'All auto-generated')
          : generatedCount === 0
            ? (excludedCount === 1 ? 'Excluded opportunity' : 'All excluded opportunities')
            : `${generatedCount} auto-generated · ${excludedCount} excluded`;

        // "Rolvedon - Flat/Base - Rebate" -> "Flat/Base Rebate"; the title already names the
        // family. Falls back to the raw string if the shape is unexpected.
        const opportunityName = (r: RebateDetail) => {
          const raw = (r.opportunity ?? '').trim();
          if (!raw) return r.contract_opportunities_id ? `Contract Opportunity ${r.contract_opportunities_id}` : 'Rebate';
          const pf = target.pfLabel.trim();
          let name = raw;
          if (pf && name.toLowerCase().startsWith(`${pf.toLowerCase()} - `)) {
            name = name.slice(pf.length + 3);
          }
          return name.split(' - ').join(' ') || raw;
        };

        // unit_of_measure is the basis the contract measures in (mg, EA, WAC...), not the unit
        // these figures carry: the SP divides by UnitEquivalency first, so a unit-measured
        // min_calc and total_units arrive already in contract units. Appending the basis gave
        // "2,000 mg". Market-share legs carry no unit at all — see Measure below.
        const CONTRACT_UNITS = 'Contract Units';

        // Thresholds AND measured volumes are display strings that come in two kinds. A
        // Market Share leg measures in percent — min_calc '75.00%' against total_units
        // '5.07%' (the modeled share of the basket) — while every other opportunity type
        // measures in contract units, '2,000' against '441.00'. Both arrive as text from the
        // SP with the '%' as the only discriminator, so each is parsed once into its kind and
        // carried as that. Comparing across kinds is meaningless and never done.
        type Measure = { kind: 'units' | 'percent'; value: number; text: string };

        const parseMeasure = (raw: string | null | undefined): Measure | null => {
          if (raw == null) return null;
          const s = String(raw).trim();
          if (!s) return null;
          const isPercent = s.includes('%');
          const n = Number(s.replace(/[%,\s]/g, ''));
          if (!Number.isFinite(n)) return null;
          return isPercent
            // Shown as the SP formatted it, like min_calc: reformatting a share invents
            // precision the modelling did not claim.
            ? { kind: 'percent', value: n, text: s }
            : { kind: 'units', value: n, text: `${formatNumber(n)} ${CONTRACT_UNITS}` };
        };

        // A tier the modelling never measured. The SP wraps its varchar in isnull(...,'0'),
        // so those rows now arrive as the literal '0' where they used to arrive as NULL —
        // which silently disabled the fill-from-ladder below and had unreached tiers claiming
        // a measured zero. Treated as "no measurement" only when the ladder offers a real one.
        const isUnmeasured = (m: Measure | null) => m != null && m.kind === 'units' && m.value === 0;

        // The direct rebate is entered per equivalent unit, which is the category unit the
        // rest of the results page displays -- not the contract's own unit of measure, so it
        // is labelled separately from CONTRACT_UNITS above.
        const CATEGORY_UNITS_SINGULAR = 'Category Unit';
        const directModeledUnits = summaryRow?.category_units != null
          ? `${formatNumber(Number(summaryRow.category_units))} Category Units`
          : NONE;

        // Rendering both legs through here stops them drifting apart. A percent threshold is
        // a market-share target and carries no unit; a numeric one is labelled in contract
        // units — parseMeasure already decided which.
        const thresholdLabel = (
          calc: string | null | undefined,
          missed: boolean,
        ): React.ReactNode => {
          const m = parseMeasure(calc);
          if (m == null) return NONE;
          return missed ? <span className="text-orange-600">{m.text}</span> : m.text;
        };

        const measureLabel = (m: Measure | null) => m?.text ?? NONE;

        // A fraction for percentage rebates, dollars for '$ per Unit' — paid_on tells them apart.
        const perUnitRate = (r: RebateDetail) => (r.paid_on ?? '').toLowerCase().includes('per unit');

        const rateText = (r: RebateDetail) => {
          if (r.paid_as_value == null) return NONE;
          const v = Number(r.paid_as_value);
          return perUnitRate(r)
            ? `$${v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
            : `${(v * 100).toFixed(2)}%`;
        };

        // The SP stamps the measured volume onto only ONE tier row per ladder, so an unreached
        // tier has nothing of its own to report a gap against — even though the measurement is
        // identical for the whole ladder (the family's contract units, or its modeled share).
        // Checked against live data: of 39 multi-tier ladders, none carried two different
        // volumes, so reading it off whichever row has it is unambiguous.
        const ladderMeasure = (pick: (r: RebateDetail) => string | null | undefined) => {
          const byCoid = new Map<number, Measure>();
          rows.forEach(r => {
            const coid = r.contract_opportunities_id;
            const m = parseMeasure(pick(r));
            if (coid == null || m == null) return;
            const prev = byCoid.get(coid);
            // A percent wins outright: on a Market Share ladder the SP formats the share only
            // on the row where Tier = ActiveTier and leaves the rest a '0' unit count, so the
            // percent is the ladder's one real measurement. Within a kind, the largest wins,
            // as before.
            if (
              prev === undefined
              || (m.kind === 'percent' && prev.kind !== 'percent')
              || (m.kind === prev.kind && m.value > prev.value)
            ) {
              byCoid.set(coid, m);
            }
          });
          return byCoid;
        };
        const primaryVolume = ladderMeasure(r => r.total_units);
        const secondaryVolume = ladderMeasure(r => r.secondary_total_units);
        const measured = (
          r: RebateDetail,
          own: string | null | undefined,
          from: Map<number, Measure>,
        ): Measure | null => {
          const mine = parseMeasure(own);
          const ladder = r.contract_opportunities_id != null
            ? from.get(r.contract_opportunities_id) ?? null
            : null;
          // isUnmeasured, not `mine == null`: since the SP started coalescing NULL to '0',
          // "no measurement on this row" and "measured zero" look identical, and only the
          // ladder can tell them apart.
          if (mine == null || (isUnmeasured(mine) && ladder != null)) return ladder;
          return mine;
        };
        const primaryUnits = (r: RebateDetail) => measured(r, r.total_units, primaryVolume);
        const secondaryUnits = (r: RebateDetail) => measured(r, r.secondary_total_units, secondaryVolume);

        // The SP stamps the rebate onto the HIGHEST tier reached in a ladder, so that row's
        // rebate_earned > 0 is the payout tier. Lower tiers in the same ladder cleared their
        // thresholds (Achieved); higher tiers were Not reached.
        const paidTierByCoid = new Map<number, number>();
        rows.forEach(r => {
          if (Number(r.rebate_earned || 0) <= 0) return;
          const coid = r.contract_opportunities_id;
          if (coid == null || r.tier == null) return;
          const prev = paidTierByCoid.get(coid);
          if (prev === undefined || Number(r.tier) > prev) paidTierByCoid.set(coid, Number(r.tier));
        });
        type RebateStatus = 'projected' | 'achieved' | 'not-reached';
        const rebateStatusOf = (r: RebateDetail): RebateStatus => {
          if (Number(r.rebate_earned || 0) > 0) return 'projected';
          const coid = r.contract_opportunities_id;
          if (coid != null && r.tier != null) {
            const paidTier = paidTierByCoid.get(coid);
            if (paidTier != null && Number(r.tier) < paidTier) return 'achieved';
          }
          return 'not-reached';
        };
        const paidTierForRow = (r: RebateDetail): number | null => {
          const coid = r.contract_opportunities_id;
          return coid != null ? paidTierByCoid.get(coid) ?? null : null;
        };

        // Whether one leg falls short of its threshold. Only compares within a kind: units
        // against units, percent against percent. Market-share tiers could not be judged
        // short at all before this — the modeled share never reached the client — so an
        // unreached share target went unmarked.
        const isShort = (calc: string | null | undefined, m: Measure | null) => {
          const floor = parseMeasure(calc);
          if (floor == null || m == null || floor.kind !== m.kind) return false;
          return m.value < floor.value;
        };

        const fieldGroup = (title: string, items: Array<[string, React.ReactNode]>) => (
          <div className="col-12 sm:col-6 lg:col-3 py-2 px-3">
            <div className="text-xs text-500 font-semibold mb-2" style={{ letterSpacing: '0.06em' }}>
              {title}
            </div>
            {items.map(([label, value]) => (
              <div key={label} className="flex justify-content-between align-items-baseline gap-3 mb-1">
                <span className="text-600 text-sm">{label}</span>
                <span className="font-semibold text-sm text-right">{value}</span>
              </div>
            ))}
          </div>
        );

        return (
          <div>
            <div className="mb-3 p-3 surface-100 border-round flex justify-content-between align-items-start flex-wrap gap-3">
              <div>
                <div className="text-xs text-600 font-semibold" style={{ letterSpacing: '0.06em' }}>
                  TOTAL PROJECTED REBATE
                </div>
                <div className={`text-4xl font-bold ${totalEarned > 0 ? 'text-green-600' : 'text-700'}`}>
                  {formatCurrency(totalEarned)}
                </div>
              </div>
              <div className="text-right text-sm text-600">
                {/* A family can reach the drill-down on a direct rebate alone, with no contract
                    opportunity behind it -- "0 of 0 opportunities" would read as a data gap. */}
                {opportunityCount > 0
                  ? <div>{projectingCount} of {opportunityCount} opportunit{opportunityCount === 1 ? 'y' : 'ies'} projecting a rebate</div>
                  : <div>No contract opportunities for this product family</div>}
                {hasDirectRebate && (
                  <div>Includes {formatCurrency(directRebate)} direct rebate, counted once</div>
                )}
                <div className="text-500">Projected on modeled volume — actual rebates depend on realized performance.</div>
                {opportunityCount > 0 && metaText && <div>{metaText}</div>}
                {rebatesDiverge && (
                  <div className="text-orange-600 mt-1">
                    <i className="pi pi-exclamation-triangle mr-1" style={{ fontSize: '0.8rem' }} />
                    Scenario summary rebate differs: {formatCurrency(summaryRebate!)}
                  </div>
                )}
              </div>
            </div>

            {(() => {
              // Group tiers of one contract (same contract_opportunities_id) so a ladder becomes
              // a self-contained, outlined block; standalone contracts stay plain cards.
              const groups: { key: string; rows: RebateDetail[] }[] = [];
              const indexByKey = new Map<string, number>();
              sorted.forEach(r => {
                const key = opportunityKeyOf(r);
                let gi = indexByKey.get(key);
                if (gi === undefined) { gi = groups.length; indexByKey.set(key, gi); groups.push({ key, rows: [] }); }
                groups[gi].rows.push(r);
              });

              // Detail grid shared by flat cards and the tiered rebate's paying-tier summary.
              const renderDetailGrid = (r: RebateDetail, opts?: { payingTierLabel?: React.ReactNode }) => {
                const st = rebateStatusOf(r);
                const missedPrimary = st === 'not-reached' && isShort(r.min_calc, primaryUnits(r));
                const missedSecondary = st === 'not-reached' && isShort(r.secondary_min_calc, secondaryUnits(r));
                return (
                  <div className="grid m-0 border-top-1 surface-border">
                    {fieldGroup('TRIGGER', [
                      ['Type', r.trigger_type ?? NONE],
                      [opts?.payingTierLabel != null ? 'Projected tier' : 'Tier', opts?.payingTierLabel ?? (r.tier ?? 'Not tiered')],
                    ])}
                    {/* Same leg names as MODELED below, so the two groups read across: Primary
                        threshold against Primary modeled units. The group header carries the
                        "minimum" sense. */}
                    {fieldGroup('THRESHOLDS', [
                      ['Primary', thresholdLabel(r.min_calc, missedPrimary)],
                      ['Secondary', thresholdLabel(r.secondary_min_calc, missedSecondary)],
                    ])}
                    {fieldGroup('MODELED', [
                      ['Primary', measureLabel(primaryUnits(r))],
                      ['Secondary', measureLabel(secondaryUnits(r))],
                    ])}
                    {fieldGroup('PAYMENT', [
                      ['Rebate', rateText(r)],
                      ['Paid on', r.paid_on ?? NONE],
                    ])}
                  </div>
                );
              };

              // Horizontal stepper: one node per tier, plus a ghost "next tier" when the top tier
              // is the paying one. Thresholds render bare; the unit is stated in the header above.
              const renderTierStepper = (rows: RebateDetail[], noThresholdText = 'No threshold') => {
                type StepNode = { key: string; label: React.ReactNode; sub?: string; sub2?: string; rate?: string; kind: 'achieved' | 'paying' | 'unreached' };
                const nodes: StepNode[] = rows.map(r => {
                  const s = rebateStatusOf(r);
                  const kind: StepNode['kind'] = s === 'projected' ? 'paying' : s === 'achieved' ? 'achieved' : 'unreached';
                  return {
                    key: `t${r.tier ?? 'flat'}`,
                    label: r.tier ?? '•',
                    sub: r.min_calc ?? noThresholdText,
                    // Second leg of a matrix (AND) contract, shown per tier so no tier's data is hidden.
                    sub2: r.secondary_min_calc ? `+ ${r.secondary_min_calc} sec` : undefined,
                    rate: rateText(r) === NONE ? undefined : rateText(r),
                    kind,
                  };
                });
                const circle = (n: StepNode): React.CSSProperties => {
                  const base: React.CSSProperties = { width: 34, height: 34, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: '0.9rem', flex: '0 0 34px' };
                  if (n.kind === 'paying') return { ...base, background: 'var(--green-500)', color: '#fff' };
                  if (n.kind === 'achieved') return { ...base, background: 'var(--primary-color)', color: '#fff' };
                  return { ...base, background: 'var(--surface-0)', color: 'var(--surface-500)', border: '2px dashed var(--surface-400)' };
                };
                const subColor = (n: StepNode) => n.kind === 'paying' ? 'var(--green-600)' : n.kind === 'achieved' ? 'var(--primary-color)' : 'var(--text-color-secondary)';
                // A 12-tier contract at the roomy connector width overflows the dialog by a few
                // hundred pixels and puts a full-width scrollbar under the ladder. The nodes
                // themselves have to keep their width (they carry a threshold and a rate), so
                // the connectors give way instead and the whole ladder fits.
                const connectorBasis = nodes.length > 8 ? 12 : 40;

                // A 30-tier ladder is ~2,270px against ~1,010px of dialog, so it opens on tier 1
                // with the tier actually paying far off to the right. Centre that tier instead.
                //
                // scrollLeft on this one container rather than scrollIntoView, which walks up and
                // would scroll the dialog itself. Position comes from bounding rects because the
                // connectors are flex-grow, so node widths alone would not locate the node. And
                // the element is stamped once it succeeds: a callback ref is a new function every
                // render, so without that it would re-centre and fight the user's own scrolling.
                const centreOnPayingTier = (el: HTMLDivElement | null) => {
                  if (!el || el.dataset.autoScrolled) return;
                  // offsetLeft/clientWidth are layout values, so unlike getBoundingClientRect they
                  // are unaffected by any transform an ancestor may be mid-animation with. The
                  // container is position:relative, making it the nodes' offsetParent.
                  const attempt = (framesLeft: number) => {
                    if (!el.isConnected || el.dataset.autoScrolled) return;
                    const node = el.querySelector<HTMLElement>('[data-paying="true"]');
                    // Nothing paying: tier 1 is the useful view and is already the leftmost.
                    if (!node) return;
                    // Width can read as 0 while the dialog is still being laid out, and the
                    // overflow check is meaningless then -- so retry a few frames before giving up
                    // rather than silently deciding the ladder fits.
                    if (!el.clientWidth || !el.scrollWidth) {
                      if (framesLeft > 0) requestAnimationFrame(() => attempt(framesLeft - 1));
                      return;
                    }
                    const maxScroll = el.scrollWidth - el.clientWidth;
                    if (maxScroll <= 0) return;  // whole ladder already visible
                    const target = node.offsetLeft - (el.clientWidth - node.offsetWidth) / 2;
                    el.scrollLeft = Math.max(0, Math.min(target, maxScroll));
                    // Stamped only once a scroll actually happened, so a failed measurement can
                    // still be retried on a later render.
                    el.dataset.autoScrolled = '1';
                  };
                  requestAnimationFrame(() => attempt(4));
                };

                return (
                  <div
                    ref={centreOnPayingTier}
                    // A ladder reads left-to-right from tier 1, so it stays left-aligned. A single
                    // node has no progression to anchor and looks stranded in the corner.
                    className={`flex align-items-start tier-stepper-scroll${nodes.length === 1 ? ' justify-content-center' : ''}`}
                  >
                    {nodes.map((n, i) => (
                      <React.Fragment key={n.key}>
                        {i > 0 && (
                          // Segment is solid up to the cleared tiers, dashed beyond.
                          <div style={{ flex: `1 1 ${connectorBasis}px`, minWidth: connectorBasis, height: 34, display: 'flex', alignItems: 'center' }}>
                            <div style={{
                              width: '100%',
                              borderTop: (n.kind === 'achieved' || n.kind === 'paying')
                                ? '3px solid var(--primary-color)'
                                : '3px dashed var(--surface-300)',
                            }} />
                          </div>
                        )}
                        <div
                          className="flex flex-column align-items-center"
                          data-paying={n.kind === 'paying' ? 'true' : undefined}
                          style={{ flex: '0 0 auto', minWidth: 64 }}
                        >
                          <div style={circle(n)}>{n.label}</div>
                          {n.sub && <div className="text-xs font-semibold mt-2" style={{ color: subColor(n) }}>{n.sub}</div>}
                          {n.sub2 && <div className="text-xs" style={{ color: subColor(n) }}>{n.sub2}</div>}
                          {n.rate && <div className="text-xs" style={{ color: subColor(n) }}>{n.rate}</div>}
                        </div>
                      </React.Fragment>
                    ))}
                  </div>
                );
              };

              const renderContractCard = (g: { key: string; rows: RebateDetail[] }) => {
                const rows = g.rows;
                const first = rows[0];
                const isExcluded = first.rebate_source === 'Excluded';
                const isLadder = rows.length > 1;
                // Flat/Base opportunities ('Automatic Savings') carry no tier: they have no rows
                // in contractDW.ContractTiers, so the SP's left join leaves Tier null. Everything
                // else -- Volume, % of Baseline, Market Share -- has a tier number even when the
                // contract only defines one (about 490 of them do). Those single-tier contracts
                // used to render exactly like a flat rebate, hiding the fact that there IS a
                // threshold and whether the modeled volume reaches it. So the stepper is driven
                // by "does this opportunity have a tier", not by "does it have more than one".
                const isTiered = rows.some(r => r.tier != null);
                const groupEarned = rows.reduce((s, r) => s + Number(r.rebate_earned || 0), 0);
                const groupHasPay = groupEarned > 0;
                // A baseline-measured opportunity's thresholds are derived from the account's
                // baseline (official, else unofficial), so the SP returns none when the account
                // has neither. Saying "No threshold" there read as missing data. The SP doesn't send OpportunityType, but its
                // names always carry it ('% of Baseline', 'Units over Baseline').
                const noBaseline = !isExcluded
                  && /baseline/i.test(first.opportunity ?? '')
                  && rows.every(r => parseMeasure(r.min_calc) == null);
                const paidTier = paidTierForRow(first);
                const borderColor = groupHasPay ? 'var(--green-500)' : 'var(--surface-300)';
                // "Not reached" only makes sense against a threshold. An untiered flat rebate has
                // none, so a zero there is "not projected", not a missed target.
                const groupStatusLabel = groupHasPay
                  ? 'Projected'
                  : (isExcluded || !isTiered || noBaseline ? 'Not projected' : 'Not reached');
                const groupStatusSeverity = groupHasPay ? 'success' : undefined;

                // Representative row for the detail grid: the paying tier if any, else tier 1.
                // Falling back to the TOP tier meant a contract with no distribution reported its
                // highest threshold as the primary one -- tier 3 of 3, when nothing has been
                // cleared and tier 1 is the threshold that actually has to be met next.
                const payingRow = rows.find(r => Number(r.rebate_earned || 0) > 0) ?? rows[0];
                const rate = rateText(payingRow) === NONE ? null : rateText(payingRow);
                const paidOn = payingRow.paid_on ?? null;

                // Tiered groups omit the modeled units here because the stepper header states
                // them; an untiered flat rebate has no stepper, so it keeps them on the subline.
                const subline = isTiered
                  ? [paidTier != null && groupHasPay ? `at tier ${paidTier}` : null, rate && paidOn ? `${rate} of ${paidOn}` : rate].filter(Boolean).join(' · ')
                  : [rate && paidOn ? `${rate} of ${paidOn}` : rate, primaryUnits(first)?.text ?? null].filter(Boolean).join(' · ');

                // Read across the whole group, not row 0: the SP stamps the measurement onto
                // one tier and leaves '0' on the others, which had the header claiming
                // "Modeled 0 Contract Units" while the MODELED group below showed the real
                // figure. Same precedence as ladderMeasure — a percent is the ladder's one
                // real measurement, otherwise the largest count.
                const modeled = rows.reduce<Measure | null>((acc, r) => {
                  const m = primaryUnits(r);
                  if (m == null) return acc;
                  if (acc == null) return m;
                  if (m.kind === 'percent' && acc.kind !== 'percent') return m;
                  if (m.kind === acc.kind && m.value > acc.value) return m;
                  return acc;
                }, null);
                // A modeled share is a share of the contract's market basket, not of the
                // category — which is the whole reason a family modeled at 25% distribution
                // can report a 33.33% share: the contract names its own basket, and a family
                // it left out puts units into the Distribution column and not into this
                // denominator. Naming the basis is what stops the two reading as a
                // contradiction. Percent kind alone identifies a share: the SP formats a
                // measure that way for no other opportunity type.
                const isShare = modeled?.kind === 'percent';
                const modeledPhrase = isShare
                  ? `${modeled!.text} of the contract basket`
                  : modeled?.text ?? '';
                const payThresh = paidTier != null ? (rows.find(r => Number(r.tier) === paidTier)?.min_calc ?? null) : null;
                // The modeled-vs-target phrasing needs both sides in the same kind. Market
                // Share now qualifies — the SP started sending the modeled share as a percent
                // TotalUnits, so "modeled 5.07% is below its tier 1's 75.00% threshold" is a
                // comparison the client can finally make and previously had to omit.
                const comparable = (t?: string | null) => {
                  const floor = parseMeasure(t);
                  return floor != null && modeled != null && floor.kind === modeled.kind;
                };
                // Nothing paying does not mean the volume fell short: the SP can withhold a
                // rebate the volume clears (a clustered contract whose partner opportunity
                // isn't met). Claiming "below" there contradicted the numbers beside it --
                // "25,182 is below 11,628.75".
                const belowLowTier = comparable(first.min_calc)
                  && modeled!.value < parseMeasure(first.min_calc)!.value;
                // rows arrive sorted by tier, so `first` is the LOWEST tier -- the one that has to
                // be cleared next. Naming it rather than hardcoding "tier 1" also covers the
                // handful of contracts whose only tier is numbered 2.
                const lowTier = first.tier ?? 1;
                // A single-tier contract is reached or it is not; there is no ladder for it to be
                // the "highest cleared" rung of, and saying so would misdescribe the contract.
                const tierRef = (t: number | string) => (isLadder ? `tier ${t}` : `its tier ${t}`);
                // An excluded opportunity has no thresholds, so nothing is "cleared" -- the user
                // credited a tier's rate by hand. Same UI, honest wording.
                const caption = isExcluded
                  ? (groupHasPay && paidTier != null
                      ? `Credited at tier ${paidTier} — this opportunity cannot be modeled automatically, so its rebate is an estimate you entered.`
                      : 'Not credited — this opportunity cannot be modeled automatically, and no estimate was entered for it.')
                  : groupHasPay && paidTier != null
                  ? (comparable(payThresh)
                      ? `Modeled ${modeledPhrase} clears ${tierRef(paidTier)}'s ${payThresh} threshold — ${isLadder ? 'the tiered rebate pays at its highest cleared tier.' : 'this contract has a single tier, and it is reached.'}`
                      : `Pays at tier ${paidTier}${payThresh ? ` (${payThresh} target)` : ''} — ${isLadder ? "the tiered rebate's highest cleared tier." : 'this contract’s only tier.'}`)
                  : noBaseline
                  ? 'No baseline on file for this account, so this rebate can’t be projected.'
                  : (belowLowTier
                      ? `Modeled ${modeledPhrase} is below ${tierRef(lowTier)}'s ${first.min_calc} threshold — ${isLadder ? 'no tier cleared yet.' : 'its single tier isn’t reached.'}`
                      : comparable(first.min_calc)
                      ? `Modeled ${modeledPhrase} reaches ${tierRef(lowTier)}'s ${first.min_calc} threshold, but no rebate is projected for it.`
                      : isLadder
                        ? 'No tier cleared yet — this tiered rebate isn’t projecting a rebate.'
                        : 'Its single tier isn’t reached — this rebate isn’t projecting.');

                return (
                  <div key={g.key} className="mb-3 border-1 surface-border border-round overflow-hidden" style={{ borderLeft: `4px solid ${borderColor}`, background: 'var(--surface-0)' }}>
                    <div className="p-3 flex justify-content-between align-items-start flex-wrap gap-2">
                      {/* flex-1 + minWidth 0 so a long opportunity name wraps INSIDE this block
                          instead of widening it and shunting the amount onto its own line. */}
                      <div className="flex flex-column gap-1 flex-1" style={{ minWidth: 0 }}>
                        <div className="flex align-items-center gap-2 flex-wrap">
                          <span className="text-lg font-semibold">{opportunityName(first)}</span>
                          {isLadder
                            ? <Tag value={`${rows.length}-tier rebate`} severity="info" />
                            : isTiered
                              ? <Tag value="Single-tier rebate" severity="info" />
                              : <Tag value="Not tiered" severity="secondary" />}
                          <Tag value={groupStatusLabel} severity={groupStatusSeverity} />
                          {isExcluded && <Tag value="Excluded opportunity" severity="warning" />}
                        </div>
                        {/* Per opportunity, not per product family: the same family can be
                            measured in EA on one contract and mg on another. Omitted rather than
                            dashed when absent -- an excluded opportunity has no basis at all. */}
                        {first.unit_of_measure && (
                          <div className="text-sm text-600">Measured in {first.unit_of_measure}</div>
                        )}
                      </div>
                      {/* ml-auto keeps it flush right even if it does wrap: on a line of its own
                          the auto margin absorbs the free space instead of left-aligning it. */}
                      <div className="text-right ml-auto flex-shrink-0">
                        <div className={`text-2xl font-bold ${groupHasPay ? 'text-green-600' : 'text-500'}`}>
                          {groupHasPay ? formatCurrency(groupEarned) : '—'}
                        </div>
                        {subline && <div className="text-sm text-600">{subline}</div>}
                      </div>
                    </div>

                    {isTiered ? (
                      <>
                        <div className="px-3 pt-3 pb-2">
                          <div className="flex align-items-center justify-content-between mb-3">
                            <span className="text-xs font-semibold text-500" style={{ letterSpacing: '0.06em' }}>
                              {isLadder ? 'TIER PROGRESSION' : 'TIER STATUS'}
                            </span>
                            {modeled != null && (
                              <span className="text-xs text-600">
                                Modeled <span className="font-semibold">{modeled.text}</span>
                                {isShare && ' of contract basket'}
                              </span>
                            )}
                          </div>
                          {renderTierStepper(rows, noBaseline ? 'No baseline' : undefined)}
                          {caption && <div className="text-sm text-600 mt-3">{caption}</div>}
                        </div>
                        {/* One tier has nothing to project ONTO, so the grid keeps its plain "Tier"
                            row; a dash under "Projected tier" would read as missing data. */}
                        {renderDetailGrid(payingRow, isLadder ? { payingTierLabel: paidTier ?? '—' } : undefined)}
                      </>
                    ) : (
                      renderDetailGrid(first)
                    )}
                    {isExcluded && first.exclusion_reason && (
                      <div className="px-3 py-2 border-top-1 surface-border text-sm text-600">
                        <span className="font-semibold">Exclusion reason: </span>
                        {first.exclusion_reason}
                      </div>
                    )}
                  </div>
                );
              };

              // Its own line, not a contract card: no tiers, no thresholds, nothing to
              // project against -- it pays on every modeled unit. Same shape as the contract
              // cards so the two read together, with the inapplicable slots dashed like the
              // rest of the dialog. Listed first: it is the certain part of the total.
              const renderDirectRebateCard = () => (
                <div key="direct-rebate" className="mb-3 border-1 surface-border border-round overflow-hidden" style={{ borderLeft: '4px solid var(--green-500)', background: 'var(--surface-0)' }}>
                  <div className="p-3 flex justify-content-between align-items-start flex-wrap gap-2">
                    <div className="flex flex-column gap-1 flex-1" style={{ minWidth: 0 }}>
                      <div className="flex align-items-center gap-2 flex-wrap">
                        <span className="text-lg font-semibold">Direct Rebate</span>
                        <Tag value="Not tiered" severity="secondary" />
                        <Tag value="Projected" severity="success" />
                      </div>
                      {/* Says only what this line is. It used to add "applied once to the
                          product family, not per contract or tier", which read as a
                          contradiction of the "$X per Category Unit" rate beside it -- and the
                          once-per-family point belongs on the total in the header, which is the
                          only place it could be double-counted. */}
                      <div className="text-sm text-600">
                        Paid outside of any contract opportunity — earned on every category unit,
                        with no threshold to reach.
                      </div>
                    </div>
                    <div className="text-right ml-auto flex-shrink-0">
                      <div className="text-2xl font-bold text-green-600">{formatCurrency(directRebate)}</div>
                      {directRebatePerEu > 0 && (
                        <div className="text-sm text-600">
                          {`$${directRebatePerEu.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} per ${CATEGORY_UNITS_SINGULAR}`}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="grid m-0 border-top-1 surface-border">
                    {fieldGroup('TRIGGER', [
                      ['Type', 'Direct rebate'],
                      ['Tier', 'Not tiered'],
                    ])}
                    {fieldGroup('THRESHOLDS', [
                      ['Primary', NONE],
                      ['Secondary', NONE],
                    ])}
                    {fieldGroup('MODELED', [
                      ['Primary', directModeledUnits],
                      ['Secondary', NONE],
                    ])}
                    {fieldGroup('PAYMENT', [
                      ['Rebate', directRebatePerEu > 0
                        ? `$${directRebatePerEu.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                        : NONE],
                      ['Paid on', `$ per ${CATEGORY_UNITS_SINGULAR}`],
                    ])}
                  </div>
                </div>
              );

              return (
                <>
                  {hasDirectRebate && renderDirectRebateCard()}
                  {groups.map((g) => renderContractCard(g))}
                </>
              );
            })()}
          </div>
        );
      })()}
    </Dialog>
  );
};

export default RebateDetailDialog;
